import { isIconKey } from "../ui/icon-key.ts";
import { MAX_RULES_NAME_CHARS, MAX_RULES_NUMBER, RULES_ID_PATTERN, type RulesProblem } from "./types.ts";

/**
 * The checks the rules validator is built from (ADR-069 decision 5). Each reads one value at a path inside a
 * record, reports what is wrong with it against the record's table and id, and returns the value narrowed, or
 * null when it is wrong. A field a record lacks is reported once, where the record's fields are read, and every
 * check passes over its `undefined` without a second report: a present field holding `undefined` never reaches a
 * check, since the walk for foreign values refuses it first.
 */

/**
 * Where a check reports: the table, the record's id (its place as `#n` when it has no valid id, or null for the
 * table itself) and the list the problems join.
 */
export interface Place {
  readonly table: string;
  readonly id: string | null;
  readonly problems: RulesProblem[];
}

/**
 * The first value inside another that is not JSON data, and where it sits.
 */
export interface Foreign {
  readonly path: string;
  readonly what: string;
}

/**
 * Adds a problem at a place.
 *
 * @param place Where.
 * @param message What is wrong, naming the field.
 */
export function report(place: Place, message: string): void {
  place.problems.push({ table: place.table, id: place.id, message });
}

/**
 * Lists a closed set for a sentence: `a`, `a or b`, `a, b or c`.
 *
 * @param items The set.
 * @returns The words.
 */
export function listed(items: readonly string[]): string {
  return items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1] as string}`;
}

/**
 * The path of a field or an item inside a value.
 *
 * @param path The value's path, empty for a whole record.
 * @param key The field's name or the item's index.
 * @param item Whether the key is an index into a list.
 * @returns The joined path.
 */
export function pathTo(path: string, key: string | number, item = false): string {
  if (item) {
    return `${path}[${key}]`;
  }
  return path === "" ? String(key) : `${path}.${key}`;
}

/**
 * Whether a value is a plain record: an object whose prototype is `Object`'s or null, and no list.
 *
 * @param value Candidate.
 * @returns True for a plain record.
 */
export function isPlainRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * Names an object that is neither a plain record nor a plain list, by its constructor's name, read without
 * calling any getter.
 *
 * @param prototype Its prototype.
 * @returns For example `a Map`.
 */
function foreignObject(prototype: unknown): string {
  if (typeof prototype === "object" && prototype !== null) {
    const constructor: unknown = Object.getOwnPropertyDescriptor(prototype, "constructor")?.value;
    if (typeof constructor === "function" && constructor.name !== "") {
      return `a ${constructor.name}`;
    }
  }
  return "an object that is not a plain record";
}

/**
 * Finds the first value inside another that is not JSON data: a function, a symbol, a bigint, `undefined`, a
 * number that is not finite, an object that is not a plain record or list, a getter or setter, a symbol key, a
 * hole in a list, or a cycle. It reads every field through its descriptor, so it never calls a getter.
 *
 * @param value The value.
 * @param path Its path, empty for a whole record or table.
 * @param ancestors The objects on the path to it, for cycles.
 * @returns The first foreign value and its path, or null when the whole value is JSON data.
 */
export function foreignValue(value: unknown, path: string, ancestors: unknown[] = []): Foreign | null {
  switch (typeof value) {
    case "string":
    case "boolean":
      return null;
    case "number":
      return Number.isFinite(value) ? null : { path, what: "a number that is not finite" };
    case "function":
      return { path, what: "a function" };
    case "symbol":
      return { path, what: "a symbol" };
    case "bigint":
      return { path, what: "a bigint" };
    case "undefined":
      return { path, what: "undefined" };
    default:
      break;
  }
  if (typeof value !== "object" || value === null) {
    return null;
  }
  if (ancestors.includes(value)) {
    return { path, what: "a cycle" };
  }
  const list = Array.isArray(value);
  const prototype: unknown = Object.getPrototypeOf(value);
  if (list ? prototype !== Array.prototype : !isPlainRecord(value)) {
    return { path, what: foreignObject(prototype) };
  }
  if (Object.getOwnPropertySymbols(value).length > 0) {
    return { path, what: "a symbol key" };
  }
  ancestors.push(value);
  try {
    for (const key of Object.keys(value)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key) as PropertyDescriptor;
      const inner = pathTo(path, key, list);
      if (descriptor.get !== undefined || descriptor.set !== undefined) {
        return { path: inner, what: descriptor.get !== undefined ? "a getter" : "a setter" };
      }
      const found = foreignValue(descriptor.value, inner, ancestors);
      if (found !== null) {
        return found;
      }
    }
    if (list) {
      const items = value as readonly unknown[];
      for (let index = 0; index < items.length; index += 1) {
        if (!Object.hasOwn(items, index)) {
          return { path: pathTo(path, index, true), what: "undefined" };
        }
      }
    }
  } finally {
    ancestors.pop();
  }
  return null;
}

/**
 * Words a foreign value as a problem.
 *
 * @param foreign The value and its path.
 * @param whole What an empty path names: `the table` or `the record`.
 * @returns The sentence.
 */
export function foreignMessage(foreign: Foreign, whole: string): string {
  return `${foreign.path === "" ? whole : foreign.path} holds ${foreign.what}, and a table holds plain data only`;
}

/**
 * Reads a record's fields: reports each field it lacks and each it does not know, and hands the record back so
 * every field present is still checked.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path, empty for a table's record.
 * @param fields The fields such a record holds, each required.
 * @param what What such a record is called, for `is not a field of`, such as `an ability`.
 * @returns The record, or null when the value is not a record.
 */
export function fieldsOf(place: Place, value: unknown, path: string, fields: readonly string[], what: string): Readonly<Record<string, unknown>> | null {
  if (value === undefined) {
    return null;
  }
  if (!isPlainRecord(value)) {
    report(place, `${path === "" ? "the record" : path} is not a record`);
    return null;
  }
  for (const key of Object.keys(value)) {
    if (!fields.includes(key)) {
      report(place, `${pathTo(path, key)} is not a field of ${what}`);
    }
  }
  for (const field of fields) {
    if (!Object.hasOwn(value, field)) {
      report(place, `${pathTo(path, field)} is missing`);
    }
  }
  return value;
}

/**
 * Reads a list.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @returns The list, or null when it is missing or not a list.
 */
export function listOf(place: Place, value: unknown, path: string): readonly unknown[] | null {
  if (value === undefined) {
    return null;
  }
  if (!Array.isArray(value)) {
    report(place, `${path} is not a list`);
    return null;
  }
  return value;
}

/**
 * Reads a whole number within bounds.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @param min The least it may be.
 * @param max The most it may be; the largest a rules number may be when not given.
 * @returns The number, or null when it is missing, not whole or out of bounds.
 */
export function whole(place: Place, value: unknown, path: string, min: number, max = MAX_RULES_NUMBER): number | null {
  return bounded(place, value, path, min, max, "not a whole number");
}

/**
 * Reads a duration: a whole number of ticks, at least `min`.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @param min The least it may be: 0, or 1 where a duration of none means nothing or would divide.
 * @param max The most it may be; the largest a rules number may be when not given.
 * @returns The ticks, or null when missing, not a whole number of ticks, or out of bounds.
 */
export function ticks(place: Place, value: unknown, path: string, min: number, max = MAX_RULES_NUMBER): number | null {
  return bounded(place, value, path, min, max, "not a whole number of ticks");
}

/**
 * Reads a number that must be whole and within bounds. JSON's `-0` is whole, and is read as 0, so a table never
 * holds a negative zero that the canonical form would write as 0.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @param min The least it may be.
 * @param max The most it may be.
 * @param notWhole What a fraction is told.
 * @returns The number, or null.
 */
function bounded(place: Place, value: unknown, path: string, min: number, max: number, notWhole: string): number | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value !== "number") {
    report(place, `${path} is not a number`);
    return null;
  }
  if (!Number.isInteger(value)) {
    report(place, `${path} is ${value}, ${notWhole}`);
    return null;
  }
  if (value < min || value > max) {
    report(place, `${path} is ${value}, and it must be at ${value < min ? `least ${min}` : `most ${max}`}`);
    return null;
  }
  return value === 0 ? 0 : value;
}

/**
 * Reads a name a player reads: 1 to 32 characters.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @returns The name, or null.
 */
export function name(place: Place, value: unknown, path: string): string | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    report(place, `${path} is not a string`);
    return null;
  }
  if (value.length < 1 || value.length > MAX_RULES_NAME_CHARS) {
    report(place, `${path} is ${value.length} characters, not 1 to ${MAX_RULES_NAME_CHARS}`);
    return null;
  }
  return value;
}

/**
 * Reads an id, or an animation's name, which takes an id's form: 1 to 32 characters of `a-z`, `0-9` and `_`,
 * starting with a letter.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @returns The id, or null.
 */
export function identifier(place: Place, value: unknown, path: string): string | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    report(place, `${path} is not a string`);
    return null;
  }
  if (!RULES_ID_PATTERN.test(value)) {
    report(place, `${path} ${value} is not 1 to 32 characters of a-z, 0-9 and _ starting with a letter`);
    return null;
  }
  return value;
}

/**
 * Reads one of a closed set.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @param set The set.
 * @param what What a member is called, such as `a damage type`.
 * @returns The member, or null.
 */
export function oneOf<T extends string>(place: Place, value: unknown, path: string, set: readonly T[], what: string): T | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    report(place, `${path} is not a string`);
    return null;
  }
  if (!(set as readonly string[]).includes(value)) {
    report(place, `${path} is ${value}, which is not ${what} (${listed(set)})`);
    return null;
  }
  return value as T;
}

/**
 * Reads true or false.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @returns The flag, or null.
 */
export function flag(place: Place, value: unknown, path: string): boolean | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value !== "boolean") {
    report(place, `${path} is not true or false`);
    return null;
  }
  return value;
}

/**
 * Reads an icon's key (ADR-111 decision 1): null for a record that names no icon, or a key of the form the icon table
 * and the icons' pointer hold theirs to. The form alone is checked: whether the icon table has a row for the key is
 * held outside this package, which cannot read the table.
 *
 * @param place Where to report.
 * @param value The value.
 * @returns The key, null for none, or undefined when it is missing or wrong.
 */
export function iconOf(place: Place, value: unknown): string | null | undefined {
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }
  if (!isIconKey(value)) {
    report(place, `icon ${String(value)} is not an icon's key (1 to 48 characters of a-z, 0-9 and - starting with a letter) or null`);
    return undefined;
  }
  return value;
}
